import { Article } from '@/components/article';
import { Container, Main, Title } from '@/components/container';
import Layout from '@/components/layout';
import ShareButtonList from '@/components/shareButton';
import SubInfo from '@/components/subInfo';
import {
  TableOfContents,
  TableOfContentsHighlighter,
} from '@/components/tableOfContents';
import { graphql } from 'gatsby';
import React, {
  forwardRef,
  MutableRefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import media from 'styled-media-query';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 66%;
`;

const Canvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const WorkContainer = styled(Container)`
  grid-template:
    'title title title' auto
    'share subInfo tableOfContents' 3rem
    'share main tableOfContents' auto / 1fr 40rem 1fr;
  ${media.lessThan('large')`
    grid-template:
      'title title' auto
      'share subInfo' auto
      'share tableOfContents' auto
      'share main' auto / auto 40rem;
  `}
  ${media.lessThan('medium')`
    grid-template:
      'title' auto
      'subInfo' auto
      'tableOfContents' auto
      'main' auto
      'share' auto / var(--main-width);
  `}
`;
const Button = forwardRef<
  HTMLButtonElement,
  {
    className?: string;
  }
>(({ className }, ref) => (
  <button type="button" className={className} ref={ref}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="20"
      viewBox="0 0 24 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="butt"
      strokeLinejoin="miter"
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  </button>
));
const FullScreen = styled(Button)`
  background-color: #fff0;
  color: white;
  border: none;
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 1rem;
  :hover {
    &::before {
      content: 'full screen';
      display: block;
      position: absolute;
      top: -2rem;
      left: 0;
      background: black;
    }
    & > svg {
      transition: transform 1s;
      transform: scale(1.5);
    }
  }
`;

const Display = ({
  canvasRef,
  buttonRef,
}: {
  canvasRef: MutableRefObject<HTMLCanvasElement>;
  buttonRef: MutableRefObject<HTMLButtonElement>;
}) => (
  <Wrapper>
    <Canvas ref={canvasRef} />
    <FullScreen ref={buttonRef} />
  </Wrapper>
);

type Props = {
  data: GatsbyTypes.WorkQuery;
};

const WorkPage: React.VFC<Props> = ({
  data: {
    site: { siteMetadata },
    file: {
      name,
      childMarkdownRemark: {
        excerpt,
        html,
        frontmatter,
        tableOfContents,
        fields,
      },
    },
  },
}) => {
  const [display, setDisplay] = useState(<div></div>);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tableOfContentsRef = useRef<HTMLDivElement>(null);

  let work: any;
  const load = async () => {
    import(`../../node_modules/@kkngsm/works/src/${fields.slug}`)
      .then(({ default: Work }: any) => {
        setDisplay(<Display canvasRef={canvasRef} buttonRef={buttonRef} />);
        Work.build(canvasRef.current).then((w: any) => {
          work = w;
          work.render(0);
          buttonRef.current.onclick = () => {
            work.fullscreen();
          };
          [``, `webkit`, `moz`, `ms`].forEach((prefix) =>
            document.addEventListener(`${prefix}fullscreenchange`, () => {
              work.cancelFullscreen();
            }),
          );
        });
        TableOfContentsHighlighter(tableOfContentsRef);
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.log(error);
        }
        TableOfContentsHighlighter(tableOfContentsRef);
      });
  };
  useEffect(() => {
    load();
    return () => {
      if (work) {
        work.remove();
      }
    };
  }, []);
  return (
    <Layout type="work" desc={excerpt} siteMetadata={siteMetadata}>
      <WorkContainer>
        <ShareButtonList
          title={frontmatter.title}
          url={`${siteMetadata.siteUrl}/posts/${name}`}
        />
        <Title>{frontmatter.title}</Title>
        <SubInfo
          date={frontmatter.date}
          tags={frontmatter.tags as string[]}
          type="work"
        />
        <Main>
          {display}
          <Article html={html} type="work" />
        </Main>
        <TableOfContents
          tableOfContents={tableOfContents}
          ref={tableOfContentsRef}
        />
      </WorkContainer>
    </Layout>
  );
};

export const query = graphql`
  query Work($slug: String!) {
    file(sourceInstanceName: { eq: "works" }, name: { eq: $slug }) {
      childMarkdownRemark {
        html
        excerpt(format: PLAIN, pruneLength: 100, truncate: true)
        tableOfContents
        frontmatter {
          title
          date(formatString: "YYYY/MM/DD")
          tags
          thumbnail {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        fields {
          slug
        }
      }
    }
    site {
      siteMetadata {
        title
        description
        siteUrl
      }
    }
  }
`;

export default WorkPage;
